


import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Save, AlertCircle, X, Pencil, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { FlightDetail, TabFlightInfoProps } from "./types/MilestoneType";
import { axiosInstance } from "../../../utility/apiInstance";
import { getMinDateTime } from "./utils";
import { convertToInputDateFormat, convertToDisplayDateFormat } from "./dateUtils";
import { useRole } from "../../MangeContextProvide";
import { AIRLINE } from "./types/FirstMilestone";
import { useDashboard } from "../DashboardContext/DashboardContext";

const s: Record<string, React.CSSProperties> = {
  root: { padding: "14px 0 0" },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  colHeaders: {
    display: "grid",
    gridTemplateColumns: "100px 130px 130px 160px 160px 140px 100px",
    padding: "0 44px 0 12px",
    marginBottom: "6px",
    minWidth: "820px",
  },
  colHeader: {
    fontSize: "10.5px",
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    padding: "0 5px",
  },

  rowWrap: { position: "relative", marginBottom: "6px" },
  rowCard: {
    display: "grid",
    gridTemplateColumns: "100px 130px 130px 160px 160px 140px 100px",
    background: "#fafafa",
    border: "1px solid #ececec",
    borderRadius: "10px",
    padding: "10px 12px",
    alignItems: "start",
    transition: "border-color 0.15s, box-shadow 0.15s",
    position: "relative",
    minWidth: "820px",
  },
  rowCardSaved: { background: "#fff", borderColor: "#efefef" },
  rowCardNew: { borderLeft: "3px solid #F8B71F" },
  rowCardError: { borderColor: "#fca5a5", background: "#fffafa" },
  rowCardEditing: { background: "#fff7d6", borderLeft: "3px solid #ffb310" },
  rowCardHovered: { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cell: { padding: "0 5px" },

  carrierBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#1a1a1a",
    color: "#fff",
    fontSize: "12px",
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: "6px",
    whiteSpace: "nowrap",
  },
  carrierDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#F8B71F",
    flexShrink: 0,
  },

  val: { fontSize: "12px", color: "#374151", padding: "6px 0", lineHeight: 1.4, fontWeight: 500 },
  valNa: { fontSize: "12px", color: "#9ca3af", padding: "6px 0" },

  input: {
    border: "1px solid #e2e2e2",
    borderRadius: "6px",
    padding: "6px 8px",
    fontSize: "11.5px",
    fontFamily: "inherit",
    color: "#374151",
    background: "#fff",
    outline: "none",
    width: "100%",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputDisabled: { background: "#f9f9f9", color: "#9ca3af", cursor: "not-allowed" },
  dateInput: {
    border: "1px solid #e2e2e2",
    borderRadius: "6px",
    padding: "6px 8px",
    fontSize: "11px",
    fontFamily: "inherit",
    color: "#374151",
    background: "#fff",
    outline: "none",
    width: "100%",
    cursor: "pointer",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputError: { borderColor: "#ef4444" },

  errText: {
    color: "#ef4444",
    fontSize: "10.5px",
    marginTop: "3px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
  },

  rowActions: {

    display: "flex",
    flexDirection: "row",
    gap: "4px",
  },
  rowActionBtn: {
    width: "28px",
    height: "28px",
    borderRadius: "7px",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 0.15s",
  },

  addRowBtn: {
    background: "none",
    border: "1px dashed #d1d5db",
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    color: "#9ca3af",
    fontFamily: "inherit",
    cursor: "pointer",
    width: "100%",
    marginTop: "4px",
    transition: "border-color 0.15s, color 0.15s",
  },

  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0 0",
    borderTop: "1px solid #f0f0f0",
    marginTop: "8px",
  },
  footerLeft: { fontSize: "11.5px", color: "#9ca3af" },
  saveBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#F8B71F",
    border: "none",
    borderRadius: "7px",
    padding: "7px 16px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#1a1a1a",
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 1px 4px rgba(248,183,31,0.35)",
    transition: "background 0.15s",
  },
  saveBtnDisabled: { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed", boxShadow: "none" },
};

const selectStyles = (hasError = false) => ({
  control: (base: any, state: any) => ({
    ...base,
    fontSize: "11.5px",
    minHeight: "30px",
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#F8B71F" : "#e2e2e2",
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 2px rgba(239,68,68,0.18)"
        : "0 0 0 2px rgba(248,183,31,0.18)"
      : "none",
    "&:hover": { borderColor: hasError ? "#ef4444" : "#F8B71F" },
    borderRadius: "6px",
    background: "#fff",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "11.5px",
    background: state.isSelected ? "#F8B71F" : state.isFocused ? "#fef9c3" : "#fff",
    color: state.isSelected ? "#1a1a1a" : "#374151",
  }),
  placeholder: (base: any) => ({ ...base, fontSize: "11.5px", color: "#9ca3af" }),
  menu: (base: any) => ({ ...base, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20 }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 6px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "3px" }),
});

function ErrorHint({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={s.errText}>
      <AlertCircle size={10} />
      {message}
    </div>
  );
}

function SectionHeader({ label, meta }: { label: string; meta?: string }) {
  return (
    <div style={s.sectionHeader}>
      <div style={s.sectionTitle}>
        <div style={s.sectionAccent} />
        <span style={s.sectionLabel}>{label}</span>
      </div>
      {meta && <span style={s.rowCount}>{meta}</span>}
    </div>
  );
}

export function TabFlightInfo({
  flightsFormData,
  setFlightsFormData,
  awbNumber,
  isOnline,
}: TabFlightInfoProps) {
  const [requiredData, setRequiredData] = useState({
    shiipingLines: [] as any[],
    ports: [] as any[],
    airline: null as AIRLINE | null,
  });

  const [flightErrors, setFlightErrors] = useState<Record<string, any>>({});
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const { selectedItem } = useDashboard();

  const { setTrackingData } = useRole() as any;

  const fetchTrackingData = async () => {
    try {
      const payload = { type: "air", trackingNumber: awbNumber };
      const { data } = await axiosInstance.post("/api/v1/tracking/data", payload);
      setTrackingData(data.data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || "Something went wrong!";
      toast.error(errorMessage);
    }
  };

  const getShiipingLines = async () => {
    try {
      const { data } = await axiosInstance.get(`api/v1/allowed-airline`);
      const airlineCode = awbNumber.split("-")[0];
      const mappedData = (data?.data || []).map((element: any) => ({
        label: element?.airlineTwolettercode || "",
        value: element.airlineTwolettercode,
      }));
      const foundAirline = data?.data?.find((element: AIRLINE) => element.airlineCode === airlineCode);

      if (foundAirline) {
        setRequiredData((prev: any) => ({ ...prev, airline: foundAirline }));
      }

      setRequiredData((prev: any) => ({ ...prev, shiipingLines: mappedData }));
    } catch (error) { }
  };

  const getPorts = async () => {
    try {
      const { data } = await axiosInstance.get(`/api/v1/port`);
      const mappedData = (data?.data || []).map((element: any) => ({
        label: element?.code || "",
        value: element.code,
      }));
      setRequiredData((prev: any) => ({ ...prev, ports: mappedData }));
    } catch (error) { }
  };

  useEffect(() => {
    getShiipingLines();
    getPorts();
  }, []);

  const fetchFlights = async () => {
    if (!awbNumber) return;
    try {
      const res = await axiosInstance.get(`/api/v1/offline-flight/${awbNumber}`);
      const data: FlightDetail[] = res.data.data || [];
      if (data.length > 0) {
        setFlightsFormData(
          data.map((f, index) => ({
            _id: f._id || Date.now().toString(),
            awbNumber: f.awbNumber,
            airlineCode: f.airlineCode || "",
            flightSequence: index + 1,
            flightNumber: f.flightNumber?.split(" ")[1] || "",
            flightDate: f.flightDate || "",
            origin: f.origin || "",
            destination: f.destination || "",
            etd: convertToInputDateFormat(f.etd),
            eta: convertToInputDateFormat(f.eta),
            isNew: false,
            isReschedule: false,
            originalData: null,
          }))
        );
      } else {
        setFlightsFormData([
          {
            _id: Date.now().toString(),
            awbNumber,
            airlineCode: requiredData.airline?.airlineTwolettercode || "",
            flightSequence: 0,
            flightNumber: "",
            flightDate: "",
            origin: selectedItem?.origin || "",
            destination: selectedItem?.destination || "",
            etd: "",
            eta: "",
            isNew: true,
            isReschedule: false,
            originalData: null,
          },
        ]);
      }
    } catch (error) { }
  };

  useEffect(() => {
    if (!awbNumber || !requiredData.airline) return;
    fetchFlights();
  }, [awbNumber, requiredData.airline]);

  const handleAddFlight = () => {
    setFlightsFormData((prev: any) => [
      ...prev,
      {
        _id: Date.now().toString(),
        awbNumber,
        airlineCode: requiredData.airline?.airlineTwolettercode || "",
        flightSequence: prev.length + 1,
        flightNumber: "",
        flightDate: "",
        origin: "",
        destination: "",
        etd: "",
        eta: "",
        isNew: true,
        isReschedule: false,
        originalData: null,
      },
    ]);
  };

  const handleFlightFormChange = (
    id: string,
    field: keyof FlightDetail | string,
    value: any
  ) => {
    setFlightsFormData((prev: any) =>
      prev.map((f: any) => (f._id === id ? { ...f, [field]: value } : f))
    );

    setFlightErrors((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: "",
      },
    }));

    if (field === "etd" || field === "eta") {
      setFlightErrors((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          eta: "",
        },
      }));
    }
  };

  const handleDeleteFlight = async (id: string) => {
    const flightToDelete = flightsFormData.find((f: any) => f._id === id);
    if (flightToDelete && flightToDelete._id && !flightToDelete.isNew) {
      try {
        await axiosInstance.post(`/api/v1/offline-flight/delete/${flightToDelete._id}`, {
          awbNumber: flightToDelete.awbNumber,
        });
      } catch (error) {
        toast.error("Failed to delete flight!");
        return;
      } finally {
        fetchTrackingData();
      }
    }
    setFlightsFormData((prev: any) => prev.filter((f: any) => f._id !== id));
  };

  const validateFlights = (flightsToValidate: any[]) => {
    let isValid = true;
    const newErrors: Record<string, any> = {};

    flightsToValidate.forEach((flight: any) => {
      const errors: any = {};
      if (!flight.airlineCode) errors.airlineCode = "Airline is required";
      if (!flight.origin) errors.origin = "Origin is required";
      if (!flight.destination) errors.destination = "Destination is required";
      if (flight.origin && flight.destination && flight.origin === flight.destination) {
        errors.destination = "Destination must be different from Origin";
      }
      if (!flight.etd) errors.etd = "ETD is required";
      if (!flight.eta) errors.eta = "ETA is required";
      if (!flight.flightNumber) errors.flightNumber = "Flight number is required";

      if (flight.etd && flight.eta) {
        const etdTime = new Date(flight.etd).getTime();
        const etaTime = new Date(flight.eta).getTime();
        if (etaTime <= etdTime) errors.eta = "ETA must be after ETD";
      }

      if (Object.keys(errors).length > 0) {
        newErrors[flight._id!] = errors;
        isValid = false;
      }
    });

    setFlightErrors((prev) => ({ ...prev, ...newErrors }));
    if (!isValid) toast.error("Please fill all required fields!");
    return isValid;
  };

  const flightsAreEqual = (a: any, b: any) => {
    if (!a || !b) return false;
    return (
      (a.airlineCode || "") === (b.airlineCode || "") &&
      (a.origin || "") === (b.origin || "") &&
      (a.destination || "") === (b.destination || "") &&
      (a.etd || "") === (b.etd || "") &&
      (a.eta || "") === (b.eta || "") &&
      (a.flightNumber || "") === (b.flightNumber || "")
    );
  };

  const handleEditFlight = (id: string) => {
    const alreadyEditing = flightsFormData.some((f: any) => f.isReschedule);
    if (alreadyEditing) {
      toast.error("Finish editing current flight first!");
      return;
    }

    setFlightsFormData((prev: any) =>
      prev.map((f: any) =>
        f._id === id ? { ...f, isReschedule: true, originalData: { ...f } } : f
      )
    );
  };

  const handleCancelEdit = (id: string) => {
    setFlightsFormData((prev: any) =>
      prev.map((f: any) =>
        f._id === id && f.originalData
          ? { ...f.originalData, isReschedule: false, originalData: null }
          : f
      )
    );
    setFlightErrors((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handleSaveEditedFlight = async (id: string) => {
    const editing = flightsFormData.find((f: any) => f._id === id && f.isReschedule);
    if (!editing) return;
    if (!validateFlights([editing])) return;

    try {
      const payload = {
        awbNumber: editing.awbNumber,
        flightNumber: `${editing.airlineCode} ${editing.flightNumber}`,
        flightDate: editing.etd ? editing.etd.split("T")[0] : "",
        origin: editing.origin,
        airlineCode: editing.airlineCode,
        flightSequence: editing.flightSequence,
        destination: editing.destination,
        etd: editing.etd,
        eta: editing.eta,
      };

      await axiosInstance.patch(`/api/v1/offline-flight/update/${editing._id}`, payload);
      toast.success("Flight updated!");
      await fetchFlights();
      await fetchTrackingData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update flight!");
    }
  };

  const handleSaveNewFlights = async () => {
    const newFlights = flightsFormData.map((f: any, index: number) => ({
      ...f,
      flightSequence: index + 1
    })).filter((f: any) => f.isNew);
    if (newFlights.length === 0) return;
    if (!validateFlights(newFlights)) return;

    try {
      const promises = newFlights.map((flight: any, index: number) => {
        const payload = {
          awbNumber: flight.awbNumber,
          flightNumber: `${flight.airlineCode} ${flight.flightNumber}`,
          flightDate: flight.etd ? flight.etd.split("T")[0] : "",
          origin: flight.origin,
          airlineCode: flight.airlineCode,
          flightSequence: flight.flightSequence || index + 1,
          destination: flight.destination,
          etd: flight.etd,
          eta: flight.eta,
        };
        return axiosInstance.post(`/api/v1/offline-flight/create`, payload);
      });

      await Promise.all(promises);
      toast.success("Flights saved successfully!");
      await fetchFlights();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save flights!");
    } finally {
      fetchTrackingData();
    }
  };

  const anyNewFlights = () => flightsFormData.some((f: any) => f.isNew);
  const hasRowChanges = (id: string) => {
    const f = flightsFormData.find((x: any) => x._id === id);
    if (!f) return false;
    if (!f.originalData) return true;
    return !flightsAreEqual(f, f.originalData);
  };
  const checkToKeepDisableSaveButton = () => !anyNewFlights();

  const newCount = flightsFormData.filter((f: any) => f.isNew).length;
  const currentlyEditing = flightsFormData.find((f: any) => f.isReschedule);
  const COLS = ["Carrier", "Origin", "Destination", "ETD", "ETA", "Flight No.", "Actions"];

  return (
    <div style={s.root}>
      <SectionHeader
        label="Flight Info"
        meta={`${flightsFormData.length} flight${flightsFormData.length !== 1 ? "s" : ""}${newCount > 0 ? ` · ${newCount} pending` : ""
          }`}
      />

      {/* <div style={{ overflowX: "auto" }}> */}
      <div>
        <div style={s.colHeaders}>
          {COLS.map((h) => (
            <span key={h} style={s.colHeader}>
              {h}
            </span>
          ))}
        </div>

        {flightsFormData.map((flight: any, index: number) => {
          const isHovered = hoveredRow === flight._id;
          const hasError = !!flightErrors[flight._id] && Object.values(flightErrors[flight._id]).some(Boolean);

          const rowStyle: React.CSSProperties = {
            ...s.rowCard,
            ...(flight.isNew ? s.rowCardNew : s.rowCardSaved),
            ...(flight.isReschedule ? s.rowCardEditing : {}),
            ...(hasError ? s.rowCardError : {}),
            ...(isHovered ? s.rowCardHovered : {}),
          };

          const editable = !isOnline && (flight.isNew || flight.isReschedule);

          return (
            <div
              key={flight._id}
              style={s.rowWrap}
              onMouseEnter={() => setHoveredRow(flight._id)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <div style={rowStyle}>
                {/* Carrier */}
                <div style={s.cell}>
                  <div style={s.carrierBadge}>
                    <span style={s.carrierDot} />
                    {flight.airlineCode || "—"}
                  </div>
                </div>

                {/* Origin */}
                <div style={s.cell}>
                  {isOnline ? (
                    <div style={flight.origin ? s.val : s.valNa}>{flight.origin || "N/A"}</div>
                  ) : (
                    <>
                      <Select
                        styles={selectStyles(!!flightErrors[flight._id]?.origin)}
                        options={requiredData.ports}
                        placeholder="Origin"
                        value={requiredData.ports.find((o: any) => o.value === flight.origin) || null}
                        onChange={(selected: any) =>
                          handleFlightFormChange(flight._id!, "origin", selected?.value || "")
                        }
                        isDisabled={!editable}
                      />
                      <ErrorHint message={flightErrors[flight._id]?.origin} />
                    </>
                  )}
                </div>

                {/* Destination */}
                <div style={s.cell}>
                  {isOnline ? (
                    <div style={flight.destination ? s.val : s.valNa}>{flight.destination || "N/A"}</div>
                  ) : (
                    <>
                      <Select
                        styles={selectStyles(!!flightErrors[flight._id]?.destination)}
                        options={requiredData.ports}
                        placeholder="Destination"
                        value={requiredData.ports.find((o: any) => o.value === flight.destination) || null}
                        onChange={(selected: any) =>
                          handleFlightFormChange(flight._id!, "destination", selected?.value || "")
                        }
                        isDisabled={!editable}
                      />
                      <ErrorHint message={flightErrors[flight._id]?.destination} />
                    </>
                  )}
                </div>

                {/* ETD */}
                <div style={s.cell}>
                  {isOnline || (!flight.isNew && !flight.isReschedule) ? (
                    <div style={flight.etd ? s.val : s.valNa}>
                      {flight.etd ? convertToDisplayDateFormat(flight.etd) : "N/A"}
                    </div>
                  ) : (
                    <>
                      <input
                        type="datetime-local"
                        style={{ ...s.dateInput, ...(flightErrors[flight._id]?.etd ? s.inputError : {}) }}
                        min={getMinDateTime()}
                        value={flight.etd || ""}
                        onMouseDown={(e: any) => {
                          e.preventDefault();
                          e.target.showPicker?.();
                        }}
                        onChange={(e) => handleFlightFormChange(flight._id!, "etd", e.target.value)}
                        disabled={!editable}
                      />
                      <ErrorHint message={flightErrors[flight._id]?.etd} />
                    </>
                  )}
                </div>

                {/* ETA */}
                <div style={s.cell}>
                  {isOnline || (!flight.isNew && !flight.isReschedule) ? (
                    <div style={flight.eta ? s.val : s.valNa}>
                      {flight.eta ? convertToDisplayDateFormat(flight.eta) : "N/A"}
                    </div>
                  ) : (
                    <>
                      <input
                        type="datetime-local"
                        style={{ ...s.dateInput, ...(flightErrors[flight._id]?.eta ? s.inputError : {}) }}
                        min={flight.etd || getMinDateTime()}
                        value={flight.eta || ""}
                        onMouseDown={(e: any) => {
                          e.preventDefault();
                          e.target.showPicker?.();
                        }}
                        onChange={(e) => handleFlightFormChange(flight._id!, "eta", e.target.value)}
                        disabled={!editable}
                      />
                      <ErrorHint message={flightErrors[flight._id]?.eta} />
                    </>
                  )}
                </div>

                {/* Flight No */}
                <div style={s.cell}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input type="text" style={{ ...s.input, ...s.inputDisabled, maxWidth: 60 }} value={flight.airlineCode} disabled />
                    <input
                      type="text"
                      style={{
                        ...s.input,
                        ...(isOnline || !editable ? s.inputDisabled : {}),
                        ...(flightErrors[flight._id]?.flightNumber ? s.inputError : {}),
                      }}
                      placeholder="1234"
                      value={flight.flightNumber}
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                        handleFlightFormChange(flight._id!, "flightNumber", onlyNumbers);
                      }}
                      maxLength={4}
                      disabled={isOnline || !editable}
                    />
                  </div>
                  <ErrorHint message={flightErrors[flight._id]?.flightNumber} />
                </div>

                {/* Actions column inline for edit mode */}
                <div style={s.cell}>
                  {!isOnline && flight.isReschedule && (
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        style={{
                          ...s.rowActionBtn,
                          width: "32px",
                          height: "32px",
                          background: hasRowChanges(flight._id!) ? "#dcfce7" : "#f3f4f6",
                          color: hasRowChanges(flight._id!) ? "#16a34a" : "#9ca3af",
                          cursor: hasRowChanges(flight._id!) ? "pointer" : "not-allowed",
                        }}
                        onClick={() => handleSaveEditedFlight(flight._id!)}
                        disabled={!hasRowChanges(flight._id!)}
                        title="Save edit"
                      >
                        <Save size={14} />
                      </button>
                      <button
                        style={{ ...s.rowActionBtn, width: "32px", height: "32px", background: "#fee2e2", color: "#dc2626" }}
                        onClick={() => handleCancelEdit(flight._id!)}
                        title="Cancel edit"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {/* Floating row actions when not editing */}
                  {!isOnline && isHovered && !flight.isReschedule && (
                    <div style={s.rowActions}>
                      {!flight.isNew && (
                        <button
                          onClick={() => handleEditFlight(flight._id!)}
                          disabled={!!currentlyEditing}
                          title="Edit flight"
                        >
                          <Pencil size={14} />
                        </button>
                      )}


                      {(
                        <button
                          style={{ ...s.rowActionBtn, background: "#fee2e2", color: "#dc2626" }}
                          onClick={() => handleDeleteFlight(flight._id!)}
                          title="Delete flight"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>


            </div>
          );
        })}
      </div>

      {!isOnline && (
        <button
          style={s.addRowBtn}
          onClick={handleAddFlight}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#F8B71F";
            (e.currentTarget as HTMLButtonElement).style.color = "#374151";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#d1d5db";
            (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af";
          }}
        >
          + Add flight leg
        </button>
      )}

      {!isOnline && (
        <div style={s.footer}>
          <span style={s.footerLeft}>
            {newCount > 0 ? (
              <>
                <strong style={{ color: "#374151" }}>{newCount}</strong> unsaved change{newCount !== 1 ? "s" : ""}
              </>
            ) : (
              "All changes saved"
            )}
          </span>
          <button
            style={{ ...s.saveBtn, ...(checkToKeepDisableSaveButton() ? s.saveBtnDisabled : {}) }}
            disabled={checkToKeepDisableSaveButton()}
            onClick={handleSaveNewFlights}
          >
            <Save size={13} />
            Save Flights
          </button>
        </div>
      )}
    </div>
  );
}